import React, { useLayoutEffect, useRef, useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import {
  FieldLabel,
  TWButton,
  TWInput,
  TWModal,
  TWSelect,
} from "../../../components/ui";
import api from "../../../services/api";
import fleetApi from "../../../services/fleetApi";
import { fleetErrorText } from "../use-fleet-poll";
import { ROLES, enrollCommand } from "../fleet-utils";
import CompanyStoreSelect from "./company-store-select";
import CopyButton from "./copy-button";

const DATE_FORMAT = "MMM D, YYYY HH:mm";
const ROLE_OPTIONS = [
  { value: "", label: "Any role" },
  ...ROLES.map(({ value, label }) => ({ value, label })),
];
const EMPTY_SCOPE = { companyid: "", storeid: "" };

// The API base this console itself talks to; the env name differs per environment.
const apiBase = () => api.defaults.baseURL || window.location.origin;

// State 1: the form. State 2 (after Create): the token, shown once, in place
// and not dismissible; Done is the only way out.
const EnrollmentTokenModal = ({ open, onClose, onCreated }) => {
  const [scope, setScope] = useState(EMPTY_SCOPE);
  const [role, setRole] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { token, expiresat }
  const submitRef = useRef(false);

  // Each opening starts from a clean form (a layout effect: no painted frame
  // of the previous token).
  useLayoutEffect(() => {
    if (!open) return;
    setScope(EMPTY_SCOPE);
    setRole("");
    setNote("");
    setCreating(false);
    setCreated(null);
    submitRef.current = false;
  }, [open]);

  const create = async () => {
    if (submitRef.current) return;
    submitRef.current = true;
    setCreating(true);
    const body = {};
    if (scope.storeid) body.storeid = scope.storeid;
    if (role) body.role = role;
    if (note.trim()) body.note = note.trim();
    try {
      const res = await fleetApi.createToken(body);
      const { token, expiresat } = res.data.data;
      setCreated({ token, expiresat });
    } catch (err) {
      message.error(fleetErrorText(err, "Could not create the token"));
    } finally {
      submitRef.current = false;
      setCreating(false);
    }
  };

  const done = () => {
    // The one-time token leaves React state now, not whenever the parent
    // gets around to closing the modal.
    setCreated(null);
    onCreated?.();
    onClose();
  };

  if (created) {
    const command = enrollCommand({ token: created.token, apiUrl: apiBase() });
    return (
      <TWModal
        open={open}
        onClose={() => {}}
        dismissible={false}
        size="md"
        title="Copy your token now"
        description="This token is shown once."
        footer={
          <div className="flex justify-end">
            <TWButton variant="primary" onClick={done}>
              Done
            </TWButton>
          </div>
        }
      >
        {/* gl-block: Gleap session replay masks this block (the token and command). */}
        <div data-testid="token-block" className="space-y-4 gl-block">
          <div
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-300"
          >
            This token is shown once. It expires{" "}
            {dayjs(created.expiresat).format(DATE_FORMAT)}.
          </div>
          <div className="flex items-end gap-2">
            <TWInput
              label="Token"
              value={created.token}
              readOnly
              className="font-mono"
              wrapperClassName="flex-1"
              onFocus={(e) => e.target.select()}
            />
            <CopyButton text={created.token} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <FieldLabel className="mb-0">Enrollment command</FieldLabel>
              <CopyButton text={command} />
            </div>
            <pre
              data-testid="enroll-command"
              className="mt-1.5 overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-slate-900 p-3 text-xs text-slate-100"
            >
              {command}
            </pre>
          </div>
        </div>
      </TWModal>
    );
  }

  return (
    <TWModal
      open={open}
      onClose={creating ? () => {} : onClose}
      size="md"
      title="New enrollment token"
      description="Works once and expires in 24 hours."
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <TWButton onClick={onClose} disabled={creating}>
            Cancel
          </TWButton>
          <TWButton
            variant="primary"
            onClick={create}
            loading={creating}
            disabled={creating}
          >
            Create token
          </TWButton>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <CompanyStoreSelect
            companyId={scope.companyid}
            storeId={scope.storeid}
            onChange={setScope}
            storeMode="single"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Leave empty to assign the device later
          </p>
        </div>
        <TWSelect
          label="Role"
          options={ROLE_OPTIONS}
          value={role}
          allowClear={false}
          onChange={(value) => setRole(value || "")}
        />
        <TWInput
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Elk Grove kiosk 2"
          maxLength={200}
        />
      </div>
    </TWModal>
  );
};

export default EnrollmentTokenModal;
